import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/ListingCard";
import { Trash2, MessageSquare, Check, X, Inbox, Send, Package, UserCircle } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-[#FBF7E9] text-black",
  accepted: "bg-black text-[#E6B31E]",
  rejected: "bg-red-100 text-red-700",
};

function OfferRow({ offer, incoming, onAct }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`offer-row-${offer.id}`} className="bg-white nb-border rounded-2xl p-5 nb-shadow flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="w-16 h-16 rounded-xl bg-[#F4F4F5] nb-border overflow-hidden flex-shrink-0 flex items-center justify-center">
        {offer.listing_image ? <img src={fileUrl(offer.listing_image)} alt="" className="w-full h-full object-cover" /> : <Package className="text-black/20" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-extrabold uppercase rounded-full px-2.5 py-0.5 border-2 border-black ${STATUS_STYLE[offer.status]}`}>{offer.status}</span>
          {offer.trade_status === "completed" && <span className="text-[10px] font-extrabold uppercase rounded-full px-2.5 py-0.5 border-2 border-black bg-green-200 text-green-800">completed</span>}
        </div>
        <div className="font-display font-extrabold text-lg mt-1 truncate">{offer.listing_title}</div>
        <div className="text-sm text-black/60 font-medium truncate">
          {incoming ? `${offer.from_user_name} offers: ` : "You offered: "}{offer.offer_description} · ${Number(offer.offered_value_usd).toLocaleString()}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {incoming && offer.status === "pending" && (
          <>
            <button data-testid={`accept-${offer.id}`} onClick={() => onAct(offer.id, "accept")} className="flex items-center gap-1 bg-[#E6B31E] text-black font-bold text-sm rounded-full px-3 py-2 border-2 border-black"><Check size={15} strokeWidth={3} /> Accept</button>
            <button data-testid={`reject-${offer.id}`} onClick={() => onAct(offer.id, "reject")} className="flex items-center gap-1 bg-white text-black font-bold text-sm rounded-full px-3 py-2 border-2 border-black"><X size={15} strokeWidth={3} /> Reject</button>
          </>
        )}
        <button data-testid={`open-trade-${offer.id}`} onClick={() => navigate(`/trade/${offer.id}`)} className="flex items-center gap-1 bg-black text-[#E6B31E] font-bold text-sm rounded-full px-3 py-2 border-2 border-black">
          <MessageSquare size={15} strokeWidth={3} /> Trade room
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("listings");
  const [mine, setMine] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);

  const load = useCallback(async () => {
    const [m, i, o] = await Promise.all([
      api.get("/listings/mine"), api.get("/offers/incoming"), api.get("/offers/outgoing"),
    ]);
    setMine(m.data); setIncoming(i.data); setOutgoing(o.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/offers/${id}/${action}`);
      toast.success(action === "accept" ? "Offer accepted — trade completed, no fees!" : "Offer rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const tabs = [
    { v: "listings", l: "My Listings", icon: Package, n: mine.length },
    { v: "incoming", l: "Incoming Offers", icon: Inbox, n: incoming.length },
    { v: "outgoing", l: "Sent Offers", icon: Send, n: outgoing.length },
  ];

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-display font-black text-4xl tracking-tighter">Dashboard</h1>
        {user && (
          <Link to={`/user/${user.user_id}`} data-testid="my-profile-link" className="flex items-center gap-2 font-bold text-sm bg-white nb-border rounded-full px-4 py-2 nb-hover nb-shadow">
            <UserCircle size={16} strokeWidth={3} /> My public profile
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map((t) => (
          <button key={t.v} data-testid={`tab-${t.v}`} onClick={() => setTab(t.v)}
            className={`flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 border-2 border-black transition-colors ${tab === t.v ? "bg-black text-[#E6B31E]" : "bg-white hover:bg-[#FBF7E9]"}`}>
            <t.icon size={16} strokeWidth={3} /> {t.l}
            <span className={`rounded-full px-2 text-xs ${tab === t.v ? "bg-[#E6B31E] text-black" : "bg-black text-white"}`}>{t.n}</span>
          </button>
        ))}
      </div>

      {tab === "listings" && (
        mine.length === 0 ? <Empty text="You haven't listed anything yet." cta /> : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-start">
            {mine.map((l, i) => (
              <div key={l.id} className="relative">
                <ListingCard listing={l} index={i} />
                <button data-testid={`delete-${l.id}`} onClick={() => del(l.id)} className="absolute top-3 right-3 bg-white text-red-600 border-2 border-black rounded-full p-2 nb-shadow z-10">
                  <Trash2 size={15} strokeWidth={3} />
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "incoming" && (
        incoming.length === 0 ? <Empty text="No incoming offers yet." /> : (
          <div className="space-y-4">{incoming.map((o) => <OfferRow key={o.id} offer={o} incoming onAct={act} />)}</div>
        )
      )}

      {tab === "outgoing" && (
        outgoing.length === 0 ? <Empty text="You haven't sent any offers yet." /> : (
          <div className="space-y-4">{outgoing.map((o) => <OfferRow key={o.id} offer={o} onAct={act} />)}</div>
        )
      )}
    </div>
  );
}

function Empty({ text, cta }) {
  return (
    <div className="py-20 text-center">
      <div className="font-display font-extrabold text-2xl">{text}</div>
      {cta && <Link to="/create" className="inline-block mt-4 bg-[#E6B31E] text-black font-extrabold rounded-full px-6 py-3 border-2 border-black">List something</Link>}
    </div>
  );
}
